import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Clock, DollarSign, FileText, CalendarDays, User, Tag } from 'lucide-react';
import { useIncident, useApproveIncident, useCancelIncident } from '../hooks/useIncidents';
import { useIsCurrentUserApprover } from '../hooks/useApprovers';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { ConfirmationModal } from '@travellegacy/nexops-design-system';
import { IncidentStatusBadge } from '../components/IncidentStatusBadge';
import { DEGREE_LABELS, AGREEMENT_STATUS_LABELS, type IncidentDegree } from '../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState, useEffect } from 'react';
import { toast } from '@/shared/lib/toast';
import { useAuth } from '@/shared/contexts/AuthContext';

const getDegreeVariant = (degree: IncidentDegree): 'success' | 'default' | 'warning' | 'error' => {
  switch (degree) {
    case 'D0':
    case 'D1':
      return 'default';
    case 'D2':
      return 'warning';
    case 'D3':
    case 'D4':
    case 'D5':
      return 'error';
    default:
      return 'default';
  }
};

const getAgreementStatusVariant = (status: string): 'success' | 'default' | 'warning' | 'error' => {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'COMPLETED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'CANCELLED':
      return 'error';
    default:
      return 'default';
  }
};

export function IncidentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedDegree, setSelectedDegree] = useState<IncidentDegree | null>(null);

  const { user } = useAuth();
  const { data: incident, isLoading } = useIncident(id);
  const approveIncident = useApproveIncident();
  const cancelIncident = useCancelIncident();
  const { data: isApprover = false } = useIsCurrentUserApprover(user?.id);

  // Pré-selecionar grau sugerido quando o incidente carregar
  useEffect(() => {
    if (incident?.suggestedDegree && !selectedDegree) {
      setSelectedDegree(incident.suggestedDegree);
    }
  }, [incident, selectedDegree]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-muted-foreground">Incidente não encontrado</p>
          <Button onClick={() => navigate('/incident-management')} className="mt-4">
            Voltar para lista
          </Button>
        </div>
      </div>
    );
  }

  const handleApprove = async () => {
    if (!selectedDegree) {
      toast.error('Selecione um grau para aprovar');
      return;
    }

    try {
      await approveIncident.mutateAsync({
        id: incident.id,
        data: { appliedDegree: selectedDegree },
      });
      toast.success('Incidente aprovado com sucesso');
      setShowApprovalModal(false);
    } catch (error) {
      toast.error('Erro ao aprovar incidente');
    }
  };

  const handleCancel = async () => {
    try {
      await cancelIncident.mutateAsync({ id: incident.id });
      toast.success('Incidente cancelado com sucesso');
      setShowCancelModal(false);
      navigate('/incident-management');
    } catch (error) {
      toast.error('Erro ao cancelar incidente');
    }
  };

  const isPending = incident.status === 'PENDING_APPROVAL';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/incident-management')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Detalhes do Incidente</h1>
            <p className="text-muted-foreground">
              Registrado em {format(new Date(incident.registeredAt), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
            </p>
          </div>
        </div>
        {isPending && isApprover && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowApprovalModal(true)}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Aprovar
            </Button>
            <Button variant="outline" onClick={() => setShowCancelModal(true)}>
              <XCircle className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
          </div>
        )}
        {isPending && !isApprover && (
          <div className="text-sm text-muted-foreground">
            Aguardando aprovação de um gestor autorizado
          </div>
        )}
      </div>

      {/* Main Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colaborador e Status */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Informações do Incidente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 pb-4 border-b">
              <User className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Colaborador</p>
                <p className="font-semibold text-lg">{incident.employee?.user.name}</p>
                <p className="text-sm text-muted-foreground">{incident.employee?.user.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 pb-4 border-b">
              <Tag className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Categoria</p>
                <p className="font-semibold">{incident.category?.name}</p>
                {incident.category?.description && (
                  <p className="text-sm text-muted-foreground">{incident.category.description}</p>
                )}
              </div>
            </div>

            <div className="pb-4 border-b">
              <p className="text-sm text-muted-foreground mb-2">Descrição</p>
              <p className="text-foreground whitespace-pre-wrap">{incident.description}</p>
            </div>

            <div className="flex items-center gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Grau Anterior</p>
                <Badge variant={getDegreeVariant(incident.previousDegree)}>
                  {DEGREE_LABELS[incident.previousDegree]}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Grau Sugerido</p>
                <Badge variant={getDegreeVariant(incident.suggestedDegree)}>
                  {DEGREE_LABELS[incident.suggestedDegree]}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Grau Aplicado</p>
                <Badge variant={getDegreeVariant(incident.appliedDegree)}>
                  {DEGREE_LABELS[incident.appliedDegree]}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status e Timeline */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Status Atual</p>
                <IncidentStatusBadge incident={incident} />
              </div>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-medium mb-2">Timeline</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <p className="text-muted-foreground">
                    Registrado por {incident.registeredBy?.name}
                  </p>
                </div>
                {incident.approvedAt && incident.approvedBy && (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <p className="text-muted-foreground">
                      Aprovado por {incident.approvedBy.name}
                    </p>
                  </div>
                )}
                {incident.monitoringDeadline && (
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-orange-500" />
                    <p className="text-muted-foreground">
                      Prazo: {format(new Date(incident.monitoringDeadline), 'dd/MM/yyyy')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Impacto Financeiro */}
      {incident.financialLoss && Number(incident.financialLoss) > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Impacto Financeiro
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Prejuízo Total</p>
                <p className="text-2xl font-bold text-red-600">
                  R$ {Number(incident.financialLoss).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Crédito Utilizado</p>
                <p className="text-2xl font-bold text-blue-600">
                  R$ {Number(incident.creditUsed).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Colaborador Responsável</p>
                <p className="text-2xl font-bold text-orange-600">
                  R$ {Number(incident.employeeResponsible).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Acordo de Pagamento */}
      {incident.paymentAgreement && (
        <Card>
          <CardHeader>
            <CardTitle>Acordo de Pagamento</CardTitle>
          </CardHeader>
          <CardContent>
            {incident.paymentAgreement.status === 'PENDING' ? (
              <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                <Clock className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Acordo Pendente</p>
                  <p className="text-sm text-muted-foreground">
                    O acordo de pagamento ainda não foi definido. Aguardando configuração das parcelas.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Valor Total</p>
                  <p className="text-lg font-bold">
                    R$ {Number(incident.paymentAgreement.totalAmount || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Parcelas</p>
                  <p className="text-lg font-bold">
                    {incident.paymentAgreement.paidInstallments || 0}/{incident.paymentAgreement.installments || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Valor da Parcela</p>
                  <p className="text-lg font-bold">
                    R$ {Number(incident.paymentAgreement.installmentAmount || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={getAgreementStatusVariant(incident.paymentAgreement.status)}>
                    {AGREEMENT_STATUS_LABELS[incident.paymentAgreement.status]}
                  </Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Documento de Advertência */}
      {incident.warningDocument && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Documento de Advertência
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                {incident.warningDocument.signedAt ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <div>
                      <p className="font-medium">Documento assinado</p>
                      <p className="text-sm">
                        em {format(new Date(incident.warningDocument.signedAt), 'dd/MM/yyyy HH:mm')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-orange-600">
                    <Clock className="w-5 h-5" />
                    <p className="font-medium">Aguardando assinatura do colaborador</p>
                  </div>
                )}
              </div>
              <Link to={`/incident-management/documents/${incident.warningDocument.id}`}>
                <Button variant="outline">
                  <FileText className="w-4 h-4 mr-2" />
                  Ver Documento
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de Cancelar Incidente */}
      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancel}
        title="Cancelar Incidente"
        description="Tem certeza que deseja cancelar este incidente? Esta ação não pode ser desfeita."
        confirmText="Sim, Cancelar"
        cancelText="Não"
        isLoading={cancelIncident.isPending}
        variant="danger"
      />

      {/* Modal de Aprovação */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Aprovar Incidente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Grau Sugerido pelo Sistema</p>
                <Badge variant={getDegreeVariant(incident.suggestedDegree)} className="text-base">
                  {DEGREE_LABELS[incident.suggestedDegree]}
                </Badge>
              </div>

              <Select
                label="Selecione o grau a ser aplicado"
                value={selectedDegree || ''}
                onChange={(e) => setSelectedDegree(e.target.value as IncidentDegree)}
                options={[
                  { value: '', label: 'Selecione...' },
                  ...Object.entries(DEGREE_LABELS).map(([value, label]) => ({
                    value,
                    label,
                  })),
                ]}
              />

              <div className="flex gap-2 pt-4">
                <Button onClick={handleApprove} disabled={!selectedDegree || approveIncident.isPending}>
                  {approveIncident.isPending ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Aprovando...
                    </>
                  ) : (
                    'Confirmar Aprovação'
                  )}
                </Button>
                <Button variant="outline" onClick={() => setShowApprovalModal(false)} disabled={approveIncident.isPending}>
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
